'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Divider } from 'components/divider'
import Image from 'next/image'
import { trackCartViewed } from '@repo/analytics/amplitude/utils'
import { trackViewCart } from '@repo/analytics/google-site-tag'
import { COUNTRY_COOKIE_NAME } from 'constants/cookies'

import { SidePanelButton } from './side-panel-button'
import type { Variant, MapKey } from '../config'
import { useConfig } from '../../_hooks/use-config'

import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import { CurrencyCode } from 'codegen/mocks'

const buildGoogleEventProps = (variant: MapKey, price: string, productId: number | string) => {
  const itemProps: Record<string, string | number | string[]> = {
    item_id: productId,
    item_name: 'Subscription Plan - All Access',
    price,
    quantity: 1,
    item_category1: 'Subscriptions',
    item_category2: ['All Access'],
  }

  if (variant === 'freeTrial' || variant === 'freeTrial_usd') {
    itemProps.item_variant = 'All Access Free Trial'
  }

  return {
    items: [itemProps],
  }
}

const buildAmplitudeEventProps = (
  currency: string,
  promoCode: string,
  price: string,
  productId: number | string,
) => {
  return {
    banner_above_nav: 'no_banner',
    banner_above_nav_state: 'no_banner',
    coupon: promoCode,
    currency: currency.toLocaleUpperCase(),
    products: [
      {
        category: 'Subscriptions',
        currencyPrice: Number(price),
        image_url: false,
        name: 'Subscription Plan - All Access',
        price: Number(price),
        productId: productId,
        product_id: productId,
        quantity: 1,
        sku: productId,
        subcategory: [],
        url: 'https://www.creativefabrica.com/product/access/?attribute_content=All+Access',
      },
    ],
  }
}

export function SidePanel({ variant }: { variant: Variant }) {
  const t = useTranslations('pages.b.side_panel')

  const { promoCode, showPromo, saving, subtotal, total, productId, currency, mapKey } =
    useConfig(variant)

  const formattedSubtotal = `${currency?.symbol}${subtotal}`
  const formattedTotal = `${currency?.symbol}${total}`
  const formattedSaving = `${currency?.symbol}${saving}`

  const zeroPrice = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency?.code || CurrencyCode.Usd,
  }).format(0)

  React.useEffect(() => {
    if (currency && showPromo) {
      trackCartViewed(
        buildAmplitudeEventProps(currency.code || 'USD', promoCode, subtotal, productId),
      )
      trackViewCart(buildGoogleEventProps(mapKey, subtotal, productId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, showPromo])

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex w-full flex-col rounded p-5 shadow-[0_5px_15px_0_rgba(0,0,0,.1)]">
        <div className="mb-[10px] flex w-full justify-between">
          <p className="text-14 tracking-[0.3px]">{t('subtotal')}</p>
          <p className="text-14 font-bold">{formattedSubtotal}</p>
        </div>
        {promoCode.length > 0 ? (
          <>
            <div className="mb-2 flex w-full justify-between">
              <p className="text-14 tracking-[0.3px]">{t('promo')}</p>
            </div>
            <div className="mb-[10px] flex w-full justify-between">
              <div className="text-12 min-w-[76px] rounded bg-[#e4e5ec] px-2 py-1 font-semibold">
                {showPromo ? promoCode : <>&nbsp;</>}
              </div>
              <p className="text-14 font-bold">{`- ${formattedSaving}`}</p>
            </div>
            <span className="text-14 text-secondary-500 tracking-[0.3px]">
              <InlineStaticVariant
                conditions={[
                  {
                    type: 'cookie',
                    cookieName: COUNTRY_COOKIE_NAME,
                    expectedValue: 'US',
                    operator: 'EQUALS',
                  },
                  {
                    type: 'feature-flag',
                    featureName: 'GROW-1258-us-sales-tax-experiment-extension',
                    expectedValue: 1,
                  },
                ]}
                getVariant={({ conditionsMet }) => {
                  return conditionsMet
                    ? t('sales_tax_variant_1')
                    : t.rich('saving', {
                        price: formattedSaving,
                      })
                }}
              />
            </span>
          </>
        ) : null}

        <Divider className="mb-5 mt-[10px] bg-[#e3e1e9]" />
        <div className="mb-[10px] flex w-full items-center justify-between">
          <p className="text-14 font-bold uppercase tracking-[0.3px]">{t('total')}</p>
          <div className="flex flex-col items-center">
            <p className="text-[24px] font-bold leading-6">{formattedTotal}</p>
          </div>
        </div>

        <SidePanelButton
          label={
            variant === 'freeTrialYearly' || variant === 'freeTrialAiStudio'
              ? t('cta_variant', { price: zeroPrice })
              : t('cta')
          }
          variant={variant}
        />
        <div className="flex flex-col gap-[10px]">
          <span className="text-[11px] text-[#7b898d]">
            <InlineStaticVariant
              conditions={[
                {
                  type: 'cookie',
                  cookieName: COUNTRY_COOKIE_NAME,
                  expectedValue: 'US',
                  operator: 'EQUALS',
                },
                {
                  type: 'feature-flag',
                  featureName: 'GROW-1258-us-sales-tax-experiment-extension',
                  expectedValue: 2,
                },
              ]}
              getVariant={({ conditionsMet }) => {
                return conditionsMet ? t('sales_tax_variant_2') : t('secure')
              }}
            />
          </span>
          <Image
            src="https://www.creativefabrica.com/wp-content/themes/creativefabricanew/v2.0/images/checkout-payment-methods.png"
            width={215}
            height={25}
            alt="Payment methods"
          />
        </div>
      </div>
    </div>
  )
}
